import {
  ArrowUpRight,
  FlaskConical,
  MessageSquareHeart,
  ShieldCheck,
  Sparkles,
  type LucideIcon
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Badge } from 'v5/design-system/Badge';
import { Button } from 'v5/design-system/Button';
import {
  Popover,
  PopoverClose,
  PopoverContent,
  PopoverTrigger
} from 'v5/design-system/Popover';
import { cn } from 'v5/platform/dom/cn';

const FEEDBACK_URL =
  'https://atellio.atlassian.net/servicedesk/customer/portals';

export interface SandboxInfoPopoverProps {
  className?: string;
}

/**
 * "Sandbox" badge for the app sidebar header. Clicking it opens a popover
 * explaining the sandbox environment, anchored to the right of the badge.
 */
export function SandboxInfoPopover({ className }: SandboxInfoPopoverProps) {
  const { t } = useTranslation('core');

  return (
    <Popover modal={false}>
      <PopoverTrigger asChild>
        <Badge
          asChild
          variant="attention"
          tone="subtle"
          size="md"
          className={cn(
            'h-8 gap-2 cursor-pointer',
            // The solid attention fill turns muddy brown on dark surfaces; use a tinted glass look instead
            'dark:bg-bg-attention-emphasis/12 dark:border-border-attention-emphasis/35 dark:text-fg-attention',
            'dark:not-aria-readonly:hocus:bg-bg-attention-emphasis/22',
            className
          )}
        >
          <button type="button">
            <PulseDot />
            {t('sandbox.badge')}
          </button>
        </Badge>
      </PopoverTrigger>
      <PopoverContent
        side="right"
        align="start"
        sideOffset={12}
        collisionPadding={16}
        className="w-[360px] max-w-[calc(100vw-32px)] p-0 overflow-hidden rounded-xl shadow-xl"
      >
        <SandboxInfoContent
          badge={t('sandbox.badge')}
          title={t('sandbox.title')}
          subtitle={t('sandbox.subtitle')}
          points={[
            {
              icon: ShieldCheck,
              title: t('sandbox.isolated.title'),
              description: t('sandbox.isolated.description')
            },
            {
              icon: Sparkles,
              title: t('sandbox.early_access.title'),
              description: t('sandbox.early_access.description')
            },
            {
              icon: MessageSquareHeart,
              title: t('sandbox.feedback.title'),
              description: t('sandbox.feedback.description')
            }
          ]}
          sendFeedbackLabel={t('sandbox.send_feedback')}
          dismissLabel={t('sandbox.dismiss')}
        />
      </PopoverContent>
    </Popover>
  );
}

interface SandboxInfoPoint {
  icon: LucideIcon;
  title: string;
  description: string;
}

interface SandboxInfoContentProps {
  badge: string;
  title: string;
  subtitle: string;
  points: SandboxInfoPoint[];
  sendFeedbackLabel: string;
  dismissLabel: string;
}

function SandboxInfoContent({
  badge,
  title,
  subtitle,
  points,
  sendFeedbackLabel,
  dismissLabel
}: SandboxInfoContentProps) {
  return (
    <div className="flex flex-col">
      <header className="relative isolate overflow-hidden bg-bg-attention dark:bg-bg-attention-emphasis/12 px-5 pt-5 pb-4 border-b border-border-attention dark:border-border-attention-emphasis/30">
        {/* Hazard-tape stripes fading out to the bottom-left */}
        <div
          aria-hidden
          className={cn(
            'absolute inset-0 -z-10 opacity-15',
            'bg-[repeating-linear-gradient(-45deg,var(--color-fg-attention)_0_10px,transparent_10px_22px)]',
            '[mask-image:linear-gradient(to_bottom_left,black,transparent_65%)]'
          )}
        />
        <div className="flex items-start gap-3">
          <div className="relative shrink-0">
            <div className="flex size-11 items-center justify-center rounded-lg bg-bg-attention-emphasis text-white shadow-md rotate-[-6deg]">
              <FlaskConical className="size-6" strokeWidth={2.25} />
            </div>
            <PulseDot className="absolute -top-1 -right-1 ring-2 ring-bg-attention dark:ring-bg-default" />
          </div>
          <div className="flex flex-col gap-0.5 min-w-0">
            <span className="text-xs font-semibold uppercase tracking-widest text-fg-attention">
              {badge}
            </span>
            <h2 className="text-lg font-semibold leading-tight text-fg-default">
              {title}
            </h2>
            <p className="text-sm text-fg-muted">{subtitle}</p>
          </div>
        </div>
      </header>

      <ul className="flex flex-col gap-4 px-5 py-4">
        {points.map(({ icon: Icon, title, description }) => (
          <li key={title} className="flex gap-3">
            <div className="flex size-8 shrink-0 items-center justify-center rounded-md border border-border-default bg-bg-subtle text-fg-attention">
              <Icon className="size-4" />
            </div>
            <div className="flex flex-col gap-0.5">
              <span className="text-sm font-semibold text-fg-default">
                {title}
              </span>
              <span className="text-sm text-fg-muted leading-snug">
                {description}
              </span>
            </div>
          </li>
        ))}
      </ul>

      <footer className="flex items-center justify-end gap-2 border-t border-border-default bg-bg-subtle/50 px-5 py-3">
        <PopoverClose asChild>
          <Button variant="ghost" size="sm">
            {dismissLabel}
          </Button>
        </PopoverClose>
        <Button variant="primary" size="sm" asChild>
          <a href={FEEDBACK_URL} target="_blank" rel="noreferrer">
            {sendFeedbackLabel}
            <ArrowUpRight />
          </a>
        </Button>
      </footer>
    </div>
  );
}

function PulseDot({ className }: { className?: string }) {
  return (
    <span className={cn('relative flex size-2.5 rounded-full', className)}>
      <span className="absolute inset-0 animate-ping rounded-full bg-bg-attention-emphasis dark:bg-fg-attention opacity-60 motion-reduce:hidden" />
      <span className="relative size-full rounded-full bg-bg-attention-emphasis dark:bg-fg-attention dark:shadow-[0_0_8px_var(--color-fg-attention)]" />
    </span>
  );
}
